/*
This file is located in the main branch
Last time the file was updated (UTC): [02.07.2024 18:52:59]
Permissions to update outside the main branch: [false]
*/

import {
    DocumentTitle,
    Input,
    Pending,
    useErrorState,
    usePendingState,
} from '@finbeat/react-toolkit';
import { getHistTable, setHasHist } from 'axios/histTableApi';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { HistTableInfoData } from 'models/api/histTable';
import { useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import {
    Accordion,
    AccordionBody,
    AccordionHeader,
    AccordionItem,
    Card,
    CardBody,
} from 'reactstrap';
import './HistTable.scss';

const HistTable = () => {
    const api = useApi();
    const { hasRole } = useAuth();
    const [tableData, setTableData] = useState<HistTableInfoData | undefined>();
    const { handleRequestError, handleError } = useErrorState();
    const { setPending } = usePendingState();
    const [open, setOpen] = useState('');
    const [init, setInit] = useState(true);

    const toggle = (id: string) => {
        if (open === id) {
            setOpen('');
        } else {
            setOpen(id);
        }
    };

    const { isFetching } = useQuery('HistTable', () => getHistTable(api), {
        onSuccess: (data) => {
            setTableData(data);
            setInit(false);
        },
        enabled: init,
        onError: (e) => handleRequestError(e),
    });

    const updateHasHist = useMutation(
        'updateHasHist',
        async (data: { schemaName: string; tableName: string }) => {
            setPending(true);
            const result = await setHasHist(api, data.schemaName, data.tableName);
            setPending(false);
            return result;
        },
        {
            onError: () => handleError(),
        }
    );

    const table = useMemo(() => {
        const table: React.ReactElement[] = [];
        let i = 0;
        for (const property in tableData) {
            table.push(
                <AccordionItem key={i}>
                    <AccordionHeader targetId={i.toString()}>{property}</AccordionHeader>
                    <AccordionBody accordionId={i.toString()}>
                        {tableData[property].map((item, index) => (
                            <div key={index}>
                                <Input
                                    type="checkbox"
                                    name="isActive"
                                    checked={item.hasHist}
                                    disabled={item.hasHist || !hasRole('histtableSet')}
                                    onChange={async (e) => {
                                        setTableData((current) => {
                                            if (current) {
                                                const item = current[property];
                                                item[index].hasHist = true;
                                                current = { ...current, [property]: item };
                                            }

                                            return current;
                                        });

                                        updateHasHist.mutate({
                                            schemaName: item.schemaName,
                                            tableName: item.tableName,
                                        });
                                    }}
                                    label={item.tableName}
                                />
                            </div>
                        ))}
                    </AccordionBody>
                </AccordionItem>
            );
            i++;
        }

        return table;
    }, [tableData]);

    // ts-unsuported props
    const accordionProps = { toggle: toggle };
    const className = 'hist-table';
    return (
        <DocumentTitle code="BackOfficeHistoryTables">
            <Card className={className}>
                <CardBody className={`${className}__body`} data-testid="hist-table">
                    {isFetching ? (
                        <Pending />
                    ) : (
                        <div className={`custom-scrollbar ${className}__container`}>
                            <Accordion open={open} {...accordionProps}>
                                {table}
                            </Accordion>
                        </div>
                    )}
                </CardBody>
            </Card>
        </DocumentTitle>
    );
};

export { HistTable };
